import React from "react";
import {
  Card,
  CardContent,
  Typography,
  CardActions,
  Checkbox,
  FormControlLabel,
  Grid,
  Box,
  Link,
  useTheme,
  SvgIcon,
  Divider,
} from "@material-ui/core";
import ClearRoundedIcon from "@material-ui/icons/ClearRounded";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import { AddOnTexts, IAddOn, IAddOnActionData, LowMillage, PopupState, UspState } from "../store/AddOn";
import CheckMark from "./CheckMark";
import Popup from "../components/Popup";
import PriceTag from "./PriceTag";
import { CheckboxGroupBase } from "./CheckboxGroupBase";
import { Theme } from "../.themes";
import LargePopup from "./LargePopup";
import { useStyles } from "./Addon.style";
import StatusIndicator from "./StatusIndicator";

type AddOnProps = {
  InternalCode: string;
  Checked?: boolean;
  disabled: boolean;
  texts?: AddOnTexts;
  checkMark?: {};
  lowMillage?: LowMillage;
  popupProps?: PopupState;
  lowMillageChanged: Function;
  addOnChanged: Function;
  uspProps?: UspState;
  includedWith?: IAddOn;
};

const openPopoverKeys = ["Enter", " "];

const AddOn: React.FC<AddOnProps> = (props: AddOnProps) => {
  const theme: Theme = useTheme();
  const css = useStyles(theme);
  const variant = "h5";

  const [anchorEl, setAnchorEl] = React.useState<HTMLAnchorElement | null>(null);

  const openPopover = (event: React.MouseEvent<HTMLAnchorElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => {
    setAnchorEl(null);
  };

  if (!props.InternalCode) {
    return <React.Fragment />;
  }

  const addAddOnInModal = () => {
    props.addOnChanged({
      addOnInternalCode: props.InternalCode,
      newState: true,
    } as IAddOnActionData);
  };

  const showCheckmark = props.Checked || props.includedWith;

  const choosePopup = () => {
    if (props.popupProps) {
      return (
        <LargePopup
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={closePopover}
          title={props.popupProps.title}
          description={props.popupProps.description}
          imageUrl={props.popupProps.imageUrl}
          altText={props.popupProps.altText}
          onButtonClick={addAddOnInModal}
          hideButton={showCheckmark}
          buttonText={props.texts?.addAddOn}
        />
      );
    } else {
      return (
        <Popup
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={closePopover}
          title={props.texts?.popupTitle}
          description={props.texts?.popupText}
        />
      );
    }
  };

  const addonBody = () => {
    if (props.uspProps?.body) {
      return (
        <div className={css.addonBody}>
          <div dangerouslySetInnerHTML={{ __html: props.uspProps?.body || "" }} className={"text-area"}></div>
          {props.uspProps?.readMoreText && (
            <Link
              tabIndex={0}
              onKeyDown={(e: any) => {
                if (openPopoverKeys.includes(e.key)) openPopover(e);
              }}
              onClick={openPopover}
              style={{ cursor: "pointer", textDecoration: "underline" }}
              className={css.descriptionText}
            >
              {props.uspProps?.readMoreText ?? ""}
            </Link>
          )}
        </div>
      );
    } else {
      return (
        <Typography variant='body1' component='p' className={css.descriptionText}>
          {props.texts?.bodyText}{" "}
          {props.texts?.popupText && (
            <Link
              tabIndex={0}
              onKeyDown={(e: any) => {
                if (openPopoverKeys.includes(e.key)) openPopover(e);
              }}
              onClick={openPopover}
              style={{ cursor: "pointer", textDecoration: "underline" }}
              className={css.descriptionText}
            >
              {props.texts?.readMore ?? ""}
            </Link>
          )}
        </Typography>
      );
    }
  };

  const includedWithAddOn = () => {
    if (!props.includedWith) return null;
    const label = `${props.texts?.includedWithText} ${props.includedWith.Name}`;

    return <StatusIndicator title={label} />;
  };

  const renderPriceTags = () => {
    if (!props.texts?.price) return null;
    const standartPriceTag = <PriceTag priceText={props.texts?.price ?? ""} textClassName={css.priceText} />;

    if (!props.includedWith) return standartPriceTag;

    const splitedPriceTag = props.texts.price.split(" ");
    if (splitedPriceTag.length !== 2) return standartPriceTag;

    const findPriceIndex = splitedPriceTag.findIndex(priceTagElement => !Number.isNaN(Number(priceTagElement)));
    if (findPriceIndex === -1) return standartPriceTag;
    splitedPriceTag[findPriceIndex] = "0";
    const zeroPriceText = splitedPriceTag.join(" ");

    return (
      <span className={css.priceTagWrapper}>
        <PriceTag priceText={props.texts?.price} textClassName={css.priceText} variant='oldPrice' />
        <PriceTag priceText={zeroPriceText} textClassName={css.priceText} />
      </span>
    );
  };

  const renderAddAddon = () => {
    if (props.disabled) return null;
    return (
      <FormControlLabel
        control={
          <Checkbox
            checkedIcon={remove}
            icon={add}
            onChange={handleAddOnChange}
            checked={props.Checked ?? false}
            disabled={props.disabled ?? false}
          />
        }
        label={
          <span className={props.Checked ? css.addTextSelected : props.disabled ? css.addTextDisabled : css.addText}>
            {props.Checked ? props.texts?.dropAddOn : props.texts?.addAddOn}
          </span>
        }
      />
    );
  };

  const handleAddOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    props.addOnChanged({
      addOnInternalCode: props.InternalCode,
      newState: event.target.checked,
    } as IAddOnActionData);
  };

  const checkmark = (theme.icons?.checkmarkSelectedOptionIcon && (
    <SvgIcon fontSize='large'>{theme.icons?.checkmarkSelectedOptionIcon}</SvgIcon>
  )) ?? <CheckMark image={props.checkMark} />;

  const addIcon = (props.disabled && theme.icons?.addIconDisabled) || theme.icons?.addIcon;

  const add = (addIcon && (
    <SvgIcon color='primary' className={css.addText}>
      {addIcon}
    </SvgIcon>
  )) ?? <AddCircleIcon color='primary' className={css.addText} />;

  const remove = (theme.icons?.removeIcon && (
    <SvgIcon color='primary' className={css.addTextSelected}>
      {theme.icons?.removeIcon}
    </SvgIcon>
  )) ?? <ClearRoundedIcon color='primary' className={css.addTextSelected} />;

  return (
    <Box component='div' className={css.component} display='flex'>
      {choosePopup()}
      <Box component='div' className={css.box} display='flex'>
        <Card className={showCheckmark ? css.cardSelected : css.card} variant='outlined'>
          {showCheckmark && (
            <Box className={css.checkMark} component='div'>
              {checkmark}
            </Box>
          )}
          <CardContent className={css.content}>
            <Typography component='h5' variant={variant} className={css.header}>
              {props.texts?.header}
            </Typography>
            {addonBody()}
          </CardContent>
          <CardActions className={css.cardActions}>
            <Grid container direction='row' alignItems='center'>
              <Grid item xs={6}>
                {includedWithAddOn()}
                {renderAddAddon()}
              </Grid>
              <Grid item xs={6} className={css.price}>
                {renderPriceTags()}
              </Grid>
            </Grid>
            {props.Checked && props.lowMillage?.questionVisible && (
              <>
                <Divider />
                <CheckboxGroupBase
                  id={props.InternalCode}
                  onChange={props.lowMillageChanged}
                  values={{
                    text: props.lowMillage?.text,
                    selected: props.lowMillage?.selected,
                  }}
                  showError={props.lowMillage?.showError}
                  errorMessage={props.lowMillage?.requiredErrorMessage}
                />
              </>
            )}
          </CardActions>
        </Card>
      </Box>
    </Box>
  );
};

export default AddOn;
